import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { externalCompanyFieldHint, externalCompanyFieldLabel } from '@brimo/shared';
import { Banner, Button, Dialog, Select, TextField, Toggle, useToast } from '../../../../ds/components';
import { errorMessage } from '../../../../lib/api';
import { usePermissions } from '../../../../lib/session';
import { useInvite, useRoles } from './api';
import styles from './UsersPage.module.css';

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * „+ Nutzer einladen" (Einstellungen 27, own dialog): e-mail, first and last name, role, „Externe Firma" (D-228) and the own-billing
 * switch → `POST /invitations` with one row (`source: 'settings'`). The per-row answer of the server (invalid
 * address, existing user, role not allowed) lands on the dialog; „Externe Adresse" is shown as a hint.
 */
export function InviteDialog({ open, onClose }: InviteDialogProps) {
  const { toast } = useToast();
  const permissions = usePermissions();
  const roles = useRoles(open);
  const invite = useInvite();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [ownBilling, setOwnBilling] = useState(false);
  const [externalCompany, setExternalCompany] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  // A sales lead may only invite the roles the server marks as invitable for team leads.
  const teamOnly = permissions.is('settings.organization', 'team');
  const roleOptions = useMemo(
    () =>
      (roles.data ?? [])
        .filter((role) => !teamOnly || role.invitableByTeamLead)
        .map((role) => ({ value: role.id, label: role.name })),
    [roles.data, teamOnly],
  );

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setFirstName('');
    setLastName('');
    setOwnBilling(false);
    setExternalCompany('');
    setError(null);
    setHint(null);
    invite.reset();
  }, [open]);

  useEffect(() => {
    if (!open || roleId !== '' || roleOptions.length === 0) return;
    const advisor = (roles.data ?? []).find((role) => role.key === 'advisor');
    setRoleId(advisor?.id ?? roleOptions[0]?.value ?? '');
  }, [open, roleId, roleOptions, roles.data]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setHint(null);
    if (email.trim() === '') {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse an.');
      return;
    }
    try {
      const result = await invite.mutateAsync({
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        roleId,
        hasOwnBilling: ownBilling,
        externalCompany: externalCompany.trim() || undefined,
      });
      const row = result.rows[0];
      if (!row || row.status === 'error') {
        setError(row?.message ?? 'Die Einladung konnte nicht gesendet werden.');
        setHint(row?.hint ?? null);
        return;
      }
      toast({ message: `Einladung an ${row.email} gesendet.` });
      onClose();
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Nutzer einladen"
      title="Wen möchten Sie einladen?"
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" form="invite-user-form" loading={invite.isPending} disabled={roleId === ''}>
            Einladung senden
          </Button>
        </>
      }
    >
      <form id="invite-user-form" className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        <TextField
          label="E-Mail"
          type="email"
          required
          autoComplete="off"
          value={email}
          error={error ?? undefined}
          hint={hint ?? undefined}
          onChange={(event) => setEmail(event.target.value)}
        />
        <div className={styles.formGrid}>
          <TextField
            label="Vorname"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
          <TextField
            label="Nachname"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </div>
        <Select
          label="Rolle"
          value={roleId}
          options={roleOptions}
          onChange={(event) => setRoleId(event.target.value)}
        />
        <TextField
          label={externalCompanyFieldLabel}
          optional
          value={externalCompany}
          maxLength={120}
          hint={externalCompanyFieldHint}
          onChange={(event) => setExternalCompany(event.target.value)}
        />
        <div className={styles.toggleRow}>
          <Toggle
            size="large"
            label="Eigene Abrechnung"
            labelHidden
            checked={ownBilling}
            onChange={setOwnBilling}
          />
          <span className={styles.toggleText}>
            Eigene Abrechnung
            <span className={styles.toggleHint}>
              Die Person erhält nach der Einladung ihren eigenen Abrechnungsschritt.
            </span>
          </span>
        </div>
        {roles.isError ? <Banner tone="danger">{errorMessage(roles.error)}</Banner> : null}
      </form>
    </Dialog>
  );
}
